import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LineChart, Line, ReferenceLine,
} from 'recharts';

const toNum = (v, fb = 0) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : fb;
  if (v === null || v === undefined) return fb;
  const n = Number(`${v}`.replace(/[$,%\s,]/g, ''));
  return Number.isFinite(n) ? n : fb;
};

const sectionTitle = (label, C) => (
  <div style={{ color: C.sub, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>
    {label}
  </div>
);

const tooltipStyle = (C) => ({
  contentStyle: { background: C.card2, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', color: C.text },
  labelStyle: { color: C.text },
  itemStyle: { color: C.text },
});

export default function AnalyticsPanel({ trades, C, cardStyle }) {
  const hasTrades = trades && trades.length > 0;

  // ── Strategy Stats ──────────────────────────────────────────────────────────
  const strategyData = React.useMemo(() => {
    const map = {};
    (trades || []).forEach(t => {
      const s = t.strategy || 'Unlabeled';
      if (!map[s]) map[s] = { strategy: s, trades: 0, wins: 0, pnl: 0 };
      const pnl = toNum(t.pnl);
      map[s].trades += 1;
      map[s].pnl += pnl;
      if (pnl > 0) map[s].wins += 1;
    });
    return Object.values(map)
      .map(d => ({ ...d, winRate: d.trades > 0 ? Math.round((d.wins / d.trades) * 100) : 0 }))
      .sort((a, b) => b.trades - a.trades);
  }, [trades]);

  // ── Symbol Stats ─────────────────────────────────────────────────────────────
  const symbolData = React.useMemo(() => {
    const map = {};
    (trades || []).forEach(t => {
      const s = t.asset || 'Unknown';
      if (!map[s]) map[s] = { symbol: s, trades: 0, wins: 0, pnl: 0 };
      const pnl = toNum(t.pnl);
      map[s].trades += 1;
      map[s].pnl += pnl;
      if (pnl > 0) map[s].wins += 1;
    });
    return Object.values(map)
      .filter(d => d.trades >= 2)
      .map(d => ({ ...d, winRate: Math.round((d.wins / d.trades) * 100), pnl: parseFloat(d.pnl.toFixed(2)) }))
      .sort((a, b) => b.pnl - a.pnl)
      .slice(0, 12);
  }, [trades]);

  // ── Day of Week ───────────────────────────────────────────────────────────────
  const dowData = React.useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const map = {};
    days.forEach(d => { map[d] = { day: d, trades: 0, wins: 0, pnl: 0 }; });
    (trades || []).forEach(t => {
      if (!t.date) return;
      const dt = new Date(t.date + 'T12:00:00');
      if (isNaN(dt)) return;
      const day = days[dt.getDay() === 0 ? 6 : dt.getDay() - 1];
      const pnl = toNum(t.pnl);
      map[day].trades += 1;
      map[day].pnl += pnl;
      if (pnl > 0) map[day].wins += 1;
    });
    return days.map(d => ({ ...map[d], pnl: parseFloat(map[d].pnl.toFixed(2)), winRate: map[d].trades > 0 ? Math.round((map[d].wins / map[d].trades) * 100) : 0 }));
  }, [trades]);

  // ── Monthly Trend ─────────────────────────────────────────────────────────────
  const monthlyData = React.useMemo(() => {
    const map = {};
    (trades || []).forEach(t => {
      if (!t.date) return;
      const dt = new Date(t.date + 'T12:00:00');
      if (isNaN(dt)) return;
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const label = dt.toLocaleString('default', { month: 'short', year: '2-digit' });
      if (!map[key]) map[key] = { key, label, pnl: 0, trades: 0, wins: 0 };
      const pnl = toNum(t.pnl);
      map[key].pnl += pnl;
      map[key].trades += 1;
      if (pnl > 0) map[key].wins += 1;
    });
    return Object.values(map)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(d => ({ ...d, pnl: parseFloat(d.pnl.toFixed(2)), winRate: Math.round((d.wins / d.trades) * 100) }));
  }, [trades]);

  // ── Running Win Rate ──────────────────────────────────────────────────────────
  const runningWinRate = React.useMemo(() => {
    let wins = 0;
    return (trades || []).map((t, i) => {
      const pnl = toNum(t.pnl);
      if (pnl > 0) wins += 1;
      return { trade: i + 1, winRate: parseFloat(((wins / (i + 1)) * 100).toFixed(1)) };
    });
  }, [trades]);

  if (!hasTrades) {
    return (
      <div style={{ ...cardStyle, padding: '40px', textAlign: 'center', color: C.sub, fontSize: '14px' }}>
        No trades yet — add some trades to see analytics.
      </div>
    );
  }

  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px', marginBottom: '14px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

      {/* ── Row 1: Strategy Win Rate + Strategy P&L ── */}
      <div style={gridStyle}>
        <div style={{ ...cardStyle, padding: '20px' }}>
          {sectionTitle('Win Rate by Strategy', C)}
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={strategyData} layout="vertical" margin={{ left: 60, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
              <XAxis type="number" stroke={C.sub} tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="strategy" stroke={C.sub} tick={{ fontSize: 10 }} width={58} />
              <Tooltip {...tooltipStyle(C)} formatter={(v, n, p) => [`${v}% (${p.payload.trades} trades)`, 'Win Rate']} />
              <Bar dataKey="winRate" radius={[0, 4, 4, 0]} maxBarSize={22}>
                {strategyData.map((d, i) => (
                  <Cell key={i} fill={d.winRate >= 50 ? C.green : C.red} fillOpacity={0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...cardStyle, padding: '20px' }}>
          {sectionTitle('Net P&L by Strategy', C)}
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={strategyData} layout="vertical" margin={{ left: 60, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
              <XAxis type="number" stroke={C.sub} tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} />
              <YAxis type="category" dataKey="strategy" stroke={C.sub} tick={{ fontSize: 10 }} width={58} />
              <ReferenceLine x={0} stroke={C.border} strokeWidth={1.5} />
              <Tooltip {...tooltipStyle(C)} formatter={(v) => [`$${v.toFixed(2)}`, 'Net P&L']} />
              <Bar dataKey="pnl" radius={[0, 4, 4, 0]} maxBarSize={22}>
                {strategyData.map((d, i) => (
                  <Cell key={i} fill={d.pnl >= 0 ? C.green : C.red} fillOpacity={0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Row 2: Day of Week ── */}
      <div style={gridStyle}>
        <div style={{ ...cardStyle, padding: '20px' }}>
          {sectionTitle('P&L by Day of Week', C)}
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dowData} margin={{ right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="day" stroke={C.sub} tick={{ fontSize: 11 }} />
              <YAxis stroke={C.sub} tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} />
              <ReferenceLine y={0} stroke={C.border} strokeWidth={1.5} />
              <Tooltip {...tooltipStyle(C)} formatter={(v, n, p) => [`$${v.toFixed(2)} (${p.payload.trades} trades, ${p.payload.winRate}% WR)`, 'P&L']} />
              <Bar dataKey="pnl" radius={[4, 4, 0, 0]} maxBarSize={40}>
                {dowData.map((d, i) => (
                  <Cell key={i} fill={d.pnl >= 0 ? C.green : C.red} fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...cardStyle, padding: '20px' }}>
          {sectionTitle('Win Rate by Day of Week', C)}
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dowData} margin={{ right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="day" stroke={C.sub} tick={{ fontSize: 11 }} />
              <YAxis stroke={C.sub} tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
              <ReferenceLine y={50} stroke={C.orange} strokeDasharray="4 4" strokeWidth={1.5} />
              <Tooltip {...tooltipStyle(C)} formatter={(v, n, p) => [`${v}% (${p.payload.trades} trades)`, 'Win Rate']} />
              <Bar dataKey="winRate" radius={[4, 4, 0, 0]} maxBarSize={40}>
                {dowData.map((d, i) => (
                  <Cell key={i} fill={d.winRate >= 50 ? C.green : C.red} fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Row 3: Symbol Breakdown ── */}
      <div style={{ ...cardStyle, padding: '20px' }}>
        {sectionTitle('P&L by Symbol (top 12, min 2 trades)', C)}
        {symbolData.length === 0 ? (
          <div style={{ color: C.sub, fontSize: '13px', textAlign: 'center', padding: '20px' }}>Need at least 2 trades per symbol.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={symbolData} layout="vertical" margin={{ left: 50, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                <XAxis type="number" stroke={C.sub} tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} />
                <YAxis type="category" dataKey="symbol" stroke={C.sub} tick={{ fontSize: 10 }} width={48} />
                <ReferenceLine x={0} stroke={C.border} strokeWidth={1.5} />
                <Tooltip {...tooltipStyle(C)} formatter={(v) => [`$${v.toFixed(2)}`, 'Net P&L']} />
                <Bar dataKey="pnl" radius={[0, 4, 4, 0]} maxBarSize={18}>
                  {symbolData.map((d, i) => (
                    <Cell key={i} fill={d.pnl >= 0 ? C.green : C.red} fillOpacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ overflowY: 'auto', maxHeight: '260px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ color: C.sub, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {['Symbol', 'Trades', 'Win %', 'Net P&L'].map(h => (
                      <th key={h} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, fontWeight: '700' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {symbolData.map((d, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}22` }}>
                      <td style={{ padding: '7px 8px', color: C.text, fontWeight: '700' }}>{d.symbol}</td>
                      <td style={{ padding: '7px 8px', color: C.sub }}>{d.trades}</td>
                      <td style={{ padding: '7px 8px', color: d.winRate >= 50 ? C.greenBright : C.redBright, fontWeight: '700' }}>{d.winRate}%</td>
                      <td style={{ padding: '7px 8px', color: d.pnl >= 0 ? C.greenBright : C.redBright, fontWeight: '800' }}>{d.pnl >= 0 ? '+' : ''}${d.pnl.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Row 4: Monthly Trend + Running Win Rate ── */}
      <div style={gridStyle}>
        <div style={{ ...cardStyle, padding: '20px' }}>
          {sectionTitle('Monthly P&L Trend', C)}
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthlyData} margin={{ right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="label" stroke={C.sub} tick={{ fontSize: 10 }} />
              <YAxis stroke={C.sub} tick={{ fontSize: 10 }} tickFormatter={v => `$${v}`} />
              <ReferenceLine y={0} stroke={C.border} strokeWidth={1.5} />
              <Tooltip {...tooltipStyle(C)} formatter={(v, n, p) => [`$${v.toFixed(2)} (${p.payload.trades} trades)`, 'P&L']} />
              <Bar dataKey="pnl" radius={[4, 4, 0, 0]} maxBarSize={36}>
                {monthlyData.map((d, i) => (
                  <Cell key={i} fill={d.pnl >= 0 ? C.green : C.red} fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ ...cardStyle, padding: '20px' }}>
          {sectionTitle('Running Win Rate Over Time', C)}
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={runningWinRate} margin={{ right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="trade" stroke={C.sub} tick={{ fontSize: 10 }} label={{ value: 'Trade #', position: 'insideBottomRight', offset: -5, fontSize: 9, fill: C.sub }} />
              <YAxis stroke={C.sub} tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
              <ReferenceLine y={50} stroke={C.orange} strokeDasharray="4 4" strokeWidth={1.5} />
              <Tooltip {...tooltipStyle(C)} formatter={v => [`${v}%`, 'Win Rate']} />
              <Line type="monotone" dataKey="winRate" stroke={C.blue} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
}
